import React, { useState, useCallback } from "react";
import { Dropzone } from "../../components/Dropzone";
import { FileList } from "../../components/FileList";
import { makeId } from "@utils";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@shadcn-in-nx/ui/tooltip";
import { Globe } from "lucide-react";
import { Bluelagoon } from "@bluelagoon";

// Custom hook for public file uploads using proxy API
function usePublicUploader({ onFileUpload, config, integrationId }) {
    const [loading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const handleUpload = async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        // Get API key from config
        const apiKey = config?.apiKey || config?.workflow?.apiKey;

        if (!apiKey) {
            throw new Error("API key is required for file upload");
        }

        const response = await fetch(
            `${Bluelagoon.apiUrl}/proxy/upload?${
                integrationId ? `integrationId=${integrationId}` : ""
            }`,
            {
                method: "POST",
                headers: {
                    "x-api-key": apiKey,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || "Upload failed");
        }

        const result = await response.json();

        if (!result.success) {
            throw new Error(result.error || "Upload failed");
        }

        return {
            url: result.url,
            key: result.key,
            bucket: result.bucket,
            region: result.region,
        };
    };

    const uploadFiles = useCallback(
        async (files) => {
            setLoading(true);

            // Convert FileList to array and create initial status objects
            const newFiles = Array.from(files).map((file) => ({
                file: file?.file ? file.file : file,
                id: makeId(5),
                name: file.name,
                status: "uploading",
                progress: 0,
                error: null,
                key: null,
                params: file?.params ? file.params : {},
            }));

            // Add new files to the state
            setUploadedFiles((prev) => [...prev, ...newFiles]);

            // Process each file
            const uploadPromises = newFiles.map(async (fileObj) => {
                try {
                    const { url, key, bucket, region } = await handleUpload(
                        fileObj.file
                    );
                    const updatedFile = {
                        ...fileObj,
                        status: "completed",
                        progress: 100,
                        key,
                        url,
                        permanentUrl: url,
                        bucket,
                        region,
                    };

                    setUploadedFiles((prev) =>
                        prev.map((f) => (f.id === fileObj.id ? updatedFile : f))
                    );

                    // Call onFileUpload callback if provided
                    if (onFileUpload) {
                        await onFileUpload({
                            key,
                            url,
                            permanentUrl: url,
                            bucket,
                            region,
                            file: fileObj.file,
                            fileData: updatedFile,
                            params: fileObj.params,
                        });
                    }

                    return { success: true, id: fileObj.id };
                } catch (error) {
                    console.error(error);
                    setUploadedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileObj.id
                                ? {
                                      ...f,
                                      status: "error",
                                      error: error.message,
                                  }
                                : f
                        )
                    );
                    return { success: false, id: fileObj.id, error };
                }
            });

            try {
                await Promise.all(uploadPromises);
            } finally {
                setLoading(false);
            }
        },
        [onFileUpload, config]
    );

    const removeFile = useCallback((fileId) => {
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    }, []);

    const clearFiles = useCallback(() => {
        setUploadedFiles([]);
    }, []);

    return {
        loading,
        files: uploadedFiles,
        uploadFiles,
        removeFile,
        clearFiles,
    };
}

export default function PublicFileUpload({
    onChange,
    filesUploaded,
    config,
    copyToClipboard,
    showPublicButton = false,
    integrationId,
}) {
    const filesUploadedMapped = React.useMemo(() => {
        return (
            filesUploaded?.map((file) => ({
                ...file.file,
            })) || []
        );
    }, [filesUploaded]);

    const onFileUpload = React.useCallback(
        async (uploadData) => {
            // Create a file object that matches the expected structure
            const fileObject = {
                id: makeId(10),
                name: uploadData.file.name,
                type: uploadData.file.type,
                size: uploadData.file.size,
                url: uploadData.url,
                key: uploadData.key,
                bucket: uploadData.bucket,
                region: uploadData.region,
                isPublic: true, // Files uploaded via proxy are considered public
            };

            if (onChange) {
                onChange(fileObject);
            }
        },
        [onChange]
    );

    const { uploadFiles, files, loading } = usePublicUploader({
        onFileUpload,
        config,
        integrationId,
    });

    const renderPublicButton = (file) => {
        if (!showPublicButton) {
            return null;
        }

        // All files uploaded via proxy are considered public
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            disabled
                        >
                            <Globe className="h-4 w-4 text-green-500" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>This file is public</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    };

    return (
        <div>
            <Dropzone onChange={uploadFiles} files={files} multiple />
            <FileList
                files={filesUploadedMapped}
                isLoading={loading}
                copyToClipboard={copyToClipboard}
                renderActions={renderPublicButton}
            />
        </div>
    );
}
